import { ReactElement, useEffect, useState } from 'react';
import CcxComponentProps from '../../../core/CcxComponent';
import CmonCpuStat from '../../../types/CmonCpuStat';
import FriendlyTimestamp from '../../../types/FriendlyTimestamp';
import AppChart from '../../AppChart';
import ChartCustomTooltip from './ChartCustomTooltip';

interface Props extends CcxComponentProps {
    data?: CmonCpuStat[];
    loading: boolean;
    displaySummary?: boolean;
    height?: number;
    width?: number;
}

function CpuUsageChart({
    data,
    loading,
    displaySummary,
    testId = 'CpuUsageChart',
    width,
    height = 250,
}: Props): ReactElement {
    const [chartData, setChartData] = useState<any>([]);
    const [summary, setSummary] = useState<any>([]);

    const setChartSummary = (data: any, series: any) => {
        const max: number[] = [];
        const min: number[] = [];
        const sum: number[] = [];
        const avg: number[] = [];

        series.forEach((s: any) => {
            max[s.value] = 0;
            min[s.value] = 0;
            sum[s.value] = 0;
            avg[s.value] = 0;
        });

        data.forEach((d: any) => {
            series.forEach((s: any) => {
                sum[s.value] += d[s.value];

                min[s.value] =
                    d[s.value] < min[s.value] ? d[s.value] : min[s.value];
                max[s.value] =
                    d[s.value] > max[s.value] ? d[s.value] : max[s.value];
            });
        });

        series.forEach((s: any) => {
            avg[s.value] = sum[s.value] / data.length;
        });

        const dataSummary = series.map((s: any) => {
            return {
                serie: s.value,
                label: s.label,
                avg: `${(avg[s.value] * 100).toFixed(2)}%`,
                min: `${(min[s.value] * 100).toFixed(2)}%`,
                max: `${(max[s.value] * 100).toFixed(2)}%`,
                sum: `${(sum[s.value] * 100).toFixed(2)}%`,
            };
        });

        setSummary(dataSummary);
    };

    useEffect(() => {
        if (data && data.length > 0) {
            const formattedData = data.map((stat: CmonCpuStat) => {
                return [
                    {
                        yValue: stat.ioWait * 100,
                        series: 'IO Wait',
                        xValue: new FriendlyTimestamp(stat.created).getDate(),
                    },
                    {
                        yValue: stat.irq * 100,
                        series: 'IRQ',
                        xValue: new FriendlyTimestamp(stat.created).getDate(),
                    },
                    {
                        yValue: stat.steal * 100,
                        series: 'Steal',
                        xValue: new FriendlyTimestamp(stat.created).getDate(),
                    },
                    {
                        yValue: stat.sys * 100,
                        series: 'System',
                        xValue: new FriendlyTimestamp(stat.created).getDate(),
                    },
                    {
                        yValue: stat.user * 100,
                        series: 'User',
                        xValue: new FriendlyTimestamp(stat.created).getDate(),
                    },
                ];
            });
            setChartData(formattedData.flat());

            const summarySeries = [
                {
                    value: 'ioWait',
                    label: 'IO Wait',
                },
                {
                    value: 'irq',
                    label: 'IRQ',
                },
                {
                    value: 'steal',
                    label: 'Steal',
                },
                {
                    value: 'sys',
                    label: 'System',
                },
                {
                    value: 'user',
                    label: 'User',
                },
            ];
            setChartSummary(data, summarySeries);
        }
    }, [data]);

    const chartConfig = {
        title: {
            visible: true,
            text: 'CPU usage',
        },
        description: {
            visible: true,
            text: '',
        },
        height,
        width,
        autoFit: !width && !height,
        xField: 'xValue',
        yField: 'yValue',
        yAxis: {
            label: {
                formatter: (text: string, item: any, index: number) => {
                    return `${text}%`;
                },
            },
        },
        xAxis: {
            type: 'time',
            mask: 'HH:mm',
        },
        animation: false,
        seriesField: 'series',
        tooltip: {
            customContent: (title: any, data: any) => {
                const getTitle = () => {
                    if (!data || data.length === 0) return '';
                    return data[0]?.data?.xValue.toString();
                };

                const getContent = () => {
                    if (!data || data.length === 0) return [];
                    return data.map((d: any) => ({
                        color: d?.color,
                        label: d?.data?.series,
                        value: `${d?.data?.yValue.toFixed(2)}%`,
                    }));
                };

                const getSummary = () => {
                    if (!data || data.length === 0) return [];
                    const sum = data.reduce(
                        (accumulator: number, d: any) =>
                            accumulator + d.data.yValue,
                        0
                    );
                    const average = sum / data.length;

                    return [
                        {
                            label: 'CPU Idle',
                            value: `${(100 - sum).toFixed(2)}%`,
                        },
                        {
                            label: 'Total Usage',
                            value: `${sum.toFixed(2)}%`,
                        },
                    ];
                };

                const tooltipData = {
                    title: getTitle(),
                    content: getContent(),
                    summary: getSummary(),
                };

                return (
                    <ChartCustomTooltip
                        title={tooltipData.title}
                        content={tooltipData.content}
                        summary={tooltipData.summary}
                    />
                );
            },
        },
    };

    return (
        <AppChart
            type="area"
            data={chartData}
            config={chartConfig}
            summary={summary}
            loading={loading}
            displaySummary={displaySummary}
            testId={testId}
        />
    );
}

export default CpuUsageChart;
